import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { fetchDemoOptions, submitDemoRequest } from "../api/demoRequests";
import { productLabel, translateMessage } from "../i18n/messages";

const FALLBACK_PRODUCTS = ["Not sure yet"];
const PHONE_CODES = ["+852", "+86", "+853", "+886", "+65", "+1", "+44", "+61", "+81", "+82"];

const emptyForm = {
  name: "",
  email: "",
  company: "",
  phoneCode: "+852",
  phone: "",
  product: "Not sure yet",
  message: "",
  website: "",
};

const DemoBookingForm = () => {
  const { t } = useTranslation();
  const [form, setForm] = useState(emptyForm);
  const [products, setProducts] = useState(FALLBACK_PRODUCTS);
  const [fieldErrors, setFieldErrors] = useState({});
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let active = true;
    fetchDemoOptions()
      .then((options) => {
        if (active && Array.isArray(options) && options.length > 0) {
          setProducts(options);
        }
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, []);

  const updateField = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
    setFieldErrors((current) => ({ ...current, [name]: undefined }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    setError("");
    setSuccess("");
    setFieldErrors({});

    const localPhone = form.phone.trim();
    const payload = {
      name: form.name,
      email: form.email,
      company: form.company,
      product: form.product,
      message: form.message,
      website: form.website,
    };
    if (localPhone) payload.phone = `${form.phoneCode} ${localPhone}`;

    try {
      const result = await submitDemoRequest(payload);
      setSuccess(result.message);
      setForm(emptyForm);
    } catch (submitError) {
      setError(submitError.message);
      setFieldErrors(submitError.fields || {});
    } finally {
      setSubmitting(false);
    }
  };

  const field = (name, label, control, required = false) => (
    <div className={fieldErrors[name] ? `field ${name === "message" ? "full" : ""} has-error` : `field ${name === "message" ? "full" : ""}`}>
      <label htmlFor={name}>
        {label}
        {required ? " *" : ""}
      </label>
      {control}
      {fieldErrors[name] && (
        <span className="field-error" id={`${name}-error`}>
          {translateMessage(fieldErrors[name], t)}
        </span>
      )}
    </div>
  );

  return (
    <form className="demo-form" onSubmit={handleSubmit}>
      <h2 id="booking-title">{t("form.title")}</h2>
      <p className="form-intro">{t("form.intro")}</p>

      {success && (
        <div className="banner banner-ok" role="status">
          {translateMessage(success, t)}
        </div>
      )}
      {error && (
        <div className="banner" role="alert">
          {translateMessage(error, t)}
        </div>
      )}

      <div className="form-grid">
        {field(
          "name",
          t("form.name"),
          <input
            id="name"
            name="name"
            value={form.name}
            onChange={updateField}
            required
            autoComplete="name"
            aria-invalid={Boolean(fieldErrors.name)}
            aria-describedby={fieldErrors.name ? "name-error" : undefined}
          />,
          true
        )}
        {field(
          "phone",
          t("form.phone"),
          <div className="phone-row">
            <select
              id="phoneCode"
              name="phoneCode"
              value={form.phoneCode}
              onChange={updateField}
              aria-label={t("form.countryCode")}
            >
              {PHONE_CODES.map((code) => (
                <option key={code} value={code}>
                  {code}
                </option>
              ))}
            </select>
            <input
              id="phone"
              name="phone"
              type="tel"
              inputMode="tel"
              value={form.phone}
              onChange={updateField}
              autoComplete="tel-national"
              aria-invalid={Boolean(fieldErrors.phone)}
              aria-describedby={fieldErrors.phone ? "phone-error" : undefined}
            />
          </div>
        )}
        {field(
          "company",
          t("form.company"),
          <input
            id="company"
            name="company"
            value={form.company}
            onChange={updateField}
            autoComplete="organization"
            aria-invalid={Boolean(fieldErrors.company)}
            aria-describedby={fieldErrors.company ? "company-error" : undefined}
          />
        )}
        {field(
          "email",
          t("form.email"),
          <input
            id="email"
            name="email"
            type="email"
            value={form.email}
            onChange={updateField}
            autoComplete="email"
            aria-invalid={Boolean(fieldErrors.email)}
            aria-describedby={fieldErrors.email ? "email-error" : undefined}
          />
        )}
        <div className={fieldErrors.product ? "field has-error" : "field"}>
          <label htmlFor="product">{t("form.product")} *</label>
          <select
            id="product"
            name="product"
            value={form.product}
            onChange={updateField}
            required
            aria-invalid={Boolean(fieldErrors.product)}
            aria-describedby={fieldErrors.product ? "product-error" : undefined}
          >
            {products.map((option) => (
              <option key={option} value={option}>
                {productLabel(option, t)}
              </option>
            ))}
          </select>
          {fieldErrors.product && (
            <span className="field-error" id="product-error">
              {translateMessage(fieldErrors.product, t)}
            </span>
          )}
        </div>
        {field(
          "message",
          t("form.message"),
          <textarea
            id="message"
            name="message"
            value={form.message}
            onChange={updateField}
            rows={4}
            aria-invalid={Boolean(fieldErrors.message)}
            aria-describedby={fieldErrors.message ? "message-error" : undefined}
          />
        )}
      </div>

      <div className="honeypot" aria-hidden="true">
        <label htmlFor="website">{t("form.website")}</label>
        <input
          id="website"
          name="website"
          tabIndex={-1}
          autoComplete="off"
          value={form.website}
          onChange={updateField}
        />
      </div>

      <button className="btn btn-green" type="submit" disabled={submitting}>
        {submitting ? t("form.sending") : t("form.submit")}
      </button>
    </form>
  );
};

export default DemoBookingForm;
